'use client';

import { NotepadText, ShoppingBag } from 'lucide-react';
import { formatPrice } from '@/utils/formatters';
import Link from 'next/link';
import type { CartSaved } from '@/types/manage.types';

interface CartItemsListProps {
  items: CartSaved[];
  isLoading: boolean;
  hasError: boolean;
  isEmpty: boolean;
  onMenuLinkClick?: () => void;
}

export function CartItemsList({ items, isLoading, hasError, isEmpty, onMenuLinkClick }: CartItemsListProps) {
  return (
    <div className="flex-1 overflow-y-auto max-h-96">
      {isLoading ? (
        <div className="p-8 text-center text-gray-500">
          <p>Loading cart...</p>
        </div>
      ) : hasError ? (
        <div className="p-8 text-center text-red-500">
          <p>Error loading cart</p>
          <p className="text-sm mt-1">Cart error occurred</p>
        </div>
      ) : isEmpty ? (
        <div className="p-8 text-center text-gray-500">
          <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-50 animate-bounce repeat-[2] fill-mode-forwards" />
          <p>Your cart is empty</p>
          <p className="text-sm mt-1">
            Add some items from{' '}
            <Link href="/menu" className="text-orange-600 " onClick={onMenuLinkClick} title="Our Menu">
              our menu
            </Link>{' '}
            to get started!
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {items.map((item) => (
            <li key={item.variant_id} className="p-4 hover:bg-gray-50 transition-colors">
              <div className="flex justify-between items-start">
                <div className="flex-1 pr-4">
                  <p className="font-medium text-gray-800 leading-tight">
                    {item.product_name} × {item.quantity}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">SKU: {item.sku}</p>
                  {item.in_cart.selected_attributes?.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      {item.in_cart.selected_attributes.map((attr) => `${attr.key}: ${attr.value}`).join(', ')}
                    </p>
                  )}

                  {item.note && (
                    <div className="flex items-start mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                      <NotepadText className="w-4 h-4 mr-2 text-amber-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-amber-800 italic leading-tight">&quot;{item.note}&quot;</span>
                    </div>
                  )}
                </div>
                <div className="font-semibold text-gray-800 text-right">
                  {formatPrice(item.in_cart.price * item.quantity)}
                  <p className="text-xs text-gray-500 font-normal">{formatPrice(item.in_cart.price)} each</p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
